import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as marketingService from '../../services/marketingService';

const { FiPlus, FiEdit, FiTrash2, FiCopy, FiFileText, FiX, FiSave, FiUpload, FiDownload, FiEye } = FiIcons;

const CATEGORIES = [
  { value: '', label: '—' },
  { value: 'registration', label: 'Registration' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'notification', label: 'Notification' },
];

const LANGUAGE_OPTIONS = [
  { value: 'es', label: 'Spanish' },
  { value: 'en', label: 'English' },
];

// Predefined variables grouped for dropdown (key, label). Use snake_case for keys.
const PREDEFINED_VARIABLE_GROUPS = [
  {
    group: 'Student / Lead',
    variables: [
      { key: 'student_name', label: 'Student Name' },
      { key: 'student_email', label: 'Student Email' },
      { key: 'student_phone', label: 'Student Phone' },
    ],
  },
  {
    group: 'Event',
    variables: [
      { key: 'event_title', label: 'Event Title' },
      { key: 'event_date', label: 'Event Date' },
      { key: 'event_time', label: 'Event Time' },
      { key: 'event_location', label: 'Event Location (name + address)' },
      { key: 'event_location_name', label: 'Event Location Name' },
      { key: 'event_location_address', label: 'Event Location Address / URL' },
      { key: 'event_price', label: 'Event Price' },
      { key: 'event_description', label: 'Event Description' },
    ],
  },
  {
    group: 'Registration',
    variables: [
      { key: 'registration_date', label: 'Registration Date' },
      { key: 'payment_amount', label: 'Payment Amount' },
      { key: 'payment_status', label: 'Payment Status' },
    ],
  },
  {
    group: 'Agent',
    variables: [
      { key: 'agent_name', label: 'Agent Name' },
      { key: 'agent_email', label: 'Agent Email' },
      { key: 'agent_phone', label: 'Agent Phone' },
    ],
  },
  {
    group: 'Zoom',
    variables: [
      { key: 'zoom_link', label: 'Zoom Link' },
      { key: 'zoom_meeting_id', label: 'Zoom Meeting ID' },
      { key: 'zoom_passcode', label: 'Zoom Passcode' },
    ],
  },
  {
    group: 'Links',
    variables: [
      { key: 'complete_registration_link', label: 'Complete Registration Link' },
      { key: 'dashboard_link', label: 'Dashboard Link' },
      { key: 'calendar_link', label: 'Calendar Link' },
    ],
  },
  {
    group: 'Platform',
    variables: [
      { key: 'platform_name', label: 'Platform Name' },
      { key: 'support_email', label: 'Support Email' },
    ],
  },
];

// All predefined keys -> { key, label } for lookup
const PREDEFINED_MAP = PREDEFINED_VARIABLE_GROUPS.reduce((acc, g) => {
  g.variables.forEach((v) => { acc[v.key] = v; });
  return acc;
}, {});

// Suggested variable keys by template category (auto-suggest when category is selected)
const SUGGESTED_BY_CATEGORY = {
  registration: ['student_name', 'student_email', 'event_title', 'event_date', 'event_time', 'payment_amount', 'zoom_link', 'complete_registration_link'],
  reminder: ['student_name', 'event_title', 'event_date', 'event_time', 'zoom_link', 'calendar_link'],
  marketing: ['student_name', 'event_title', 'event_date', 'event_price', 'agent_name'],
  notification: ['student_name', 'event_title', 'agent_name', 'registration_date'],
};

const renderSampleBody = (html, variablesSchema) => {
  if (!html) return '';
  let out = html;
  const sample = (variablesSchema || []).reduce((acc, v) => {
    acc[v.key] = v.label || v.key || 'Sample';
    return acc;
  }, {});
  for (const [k, v] of Object.entries(sample)) {
    out = out.replace(new RegExp(`\\{\\{?${k}\\}?\\}`, 'g'), v);
  }
  return out;
};

const MarketingEmailTemplates = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const { profile } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: '',
    trigger_event: 'custom',
    language: 'es',
    subject: '',
    preview_text: '',
    html_body: '',
    variables_schema: [],
    json_definition: null,
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [languageFilter, setLanguageFilter] = useState('');
  const [varKey, setVarKey] = useState('');
  const [varLabel, setVarLabel] = useState('');
  const [varRequired, setVarRequired] = useState(false);
  const [predefinedSelect, setPredefinedSelect] = useState('');
  const fileInputRef = useRef(null);

  // When template category is selected, auto-suggest variables for that category (merge, no duplicates)
  useEffect(() => {
    const cat = form.category && SUGGESTED_BY_CATEGORY[form.category];
    if (!cat || !cat.length) return;
    const existingKeys = new Set((form.variables_schema || []).map((v) => v.key).filter(Boolean));
    const toAdd = cat
      .filter((k) => !existingKeys.has(k))
      .map((k) => ({
        key: k,
        label: PREDEFINED_MAP[k]?.label || k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        required: false,
      }));
    if (toAdd.length === 0) return;
    setForm((f) => ({
      ...f,
      variables_schema: [...(f.variables_schema || []), ...toAdd],
    }));
  }, [form.category]);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    const filters = [];
    if (languageFilter && languageFilter !== '') {
      filters.push({ column: 'language', operator: 'eq', value: languageFilter });
    }
    const { data, error } = await marketingService.listTemplates({
      role: 'admin',
      userId: profile?.id,
      filters: filters.length ? filters : undefined,
    });
    setLoading(false);
    if (error) {
      toast.error(error.message ?? 'Failed to load templates');
      setTemplates([]);
      return;
    }
    setTemplates(Array.isArray(data) ? data : []);
  }, [profile?.id, languageFilter]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (templateId && templates.length) {
      const t = templates.find((x) => x.id === templateId);
      if (t) {
        setEditingId(t.id);
        setForm({
          name: t.name ?? '',
          description: t.description ?? '',
          category: t.category ?? '',
          trigger_event: t.trigger_event ?? 'custom',
          language: t.language ?? 'es',
          subject: t.subject ?? '',
          preview_text: t.preview_text ?? '',
          html_body: t.html_body ?? '',
          variables_schema: Array.isArray(t.variables_schema) ? t.variables_schema : [],
          json_definition: t.json_definition ?? null,
          is_active: t.is_active ?? true,
        });
        setShowForm(true);
      }
    }
  }, [templateId, templates]);

  const openCreate = () => {
    setEditingId(null);
    setForm({
      name: '',
      description: '',
      category: '',
      trigger_event: 'custom',
      language: 'es',
      subject: '',
      preview_text: '',
      html_body: '',
      variables_schema: [],
      json_definition: null,
      is_active: true,
    });
    setShowForm(true);
    navigate('/marketing/email/templates');
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      name: t.name ?? '',
      description: t.description ?? '',
      category: t.category ?? '',
      trigger_event: t.trigger_event ?? 'custom',
      language: t.language ?? 'es',
      subject: t.subject ?? '',
      preview_text: t.preview_text ?? '',
      html_body: t.html_body ?? '',
      variables_schema: Array.isArray(t.variables_schema) ? t.variables_schema : [],
      json_definition: t.json_definition ?? null,
      is_active: t.is_active ?? true,
    });
    setShowForm(true);
    navigate(`/marketing/email/templates/${t.id}`);
  };

  const addVariable = () => {
    const key = varKey.trim().replace(/\s+/g, '_').toLowerCase();
    if (!key) return;
    const existingKeys = new Set((form.variables_schema || []).map((v) => v.key));
    if (existingKeys.has(key)) {
      toast.error('This variable is already added.');
      return;
    }
    setForm((f) => ({
      ...f,
      variables_schema: [...(f.variables_schema || []), { key, label: varLabel.trim() || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), required: varRequired }],
    }));
    setVarKey('');
    setVarLabel('');
    setVarRequired(false);
  };

  const addPredefinedVariable = (key) => {
    if (!key) return;
    const existingKeys = new Set((form.variables_schema || []).map((v) => v.key));
    if (existingKeys.has(key)) {
      toast.error('This variable is already added.');
      setPredefinedSelect('');
      return;
    }
    const def = PREDEFINED_MAP[key] || { key, label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) };
    setForm((f) => ({
      ...f,
      variables_schema: [...(f.variables_schema || []), { key: def.key, label: def.label, required: false }],
    }));
    setPredefinedSelect('');
  };

  const removeVariable = (i) => {
    setForm((f) => ({
      ...f,
      variables_schema: (f.variables_schema || []).filter((_, idx) => idx !== i),
    }));
  };

  const saveTemplate = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!form.subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    if (!form.html_body.trim()) {
      toast.error('HTML body is required');
      return;
    }
    const schema = (form.variables_schema || []).filter((v) => v && v.key);
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      category: form.category || null,
      trigger_event: form.trigger_event,
      language: form.language || 'es',
      subject: form.subject.trim(),
      preview_text: form.preview_text.trim() || null,
      html_body: form.html_body,
      variables_schema: schema.length ? schema : null,
      json_definition: form.json_definition,
      is_active: form.is_active,
    };
    const { data, error } = editingId
      ? await marketingService.updateTemplate(editingId, payload, { role: 'admin', userId: profile?.id })
      : await marketingService.createTemplate(payload, { role: 'admin', userId: profile?.id });
    setSaving(false);
    if (error) {
      toast.error(error.message ?? 'Failed to save template');
      return;
    }
    toast.success(editingId ? 'Template updated' : 'Template created');
    setShowForm(false);
    setEditingId(null);
    navigate('/marketing/email/templates');
    loadTemplates();
  };

  const duplicateTemplateRow = async (t) => {
    const { data, error } = await marketingService.duplicateTemplate(t.id, { role: 'admin', userId: profile?.id });
    if (error) {
      toast.error(error.message ?? 'Failed to duplicate template');
      return;
    }
    toast.success('Template duplicated');
    await loadTemplates();
    if (data?.id) {
      navigate(`/marketing/email/templates/${data.id}`);
      setEditingId(data.id);
      setForm({
        name: data.name ?? '',
        description: data.description ?? '',
        category: data.category ?? '',
        trigger_event: data.trigger_event ?? 'custom',
        language: data.language ?? 'es',
        subject: data.subject ?? '',
        preview_text: data.preview_text ?? '',
        html_body: data.html_body ?? '',
        variables_schema: Array.isArray(data.variables_schema) ? data.variables_schema : [],
        json_definition: data.json_definition ?? null,
        is_active: data.is_active ?? true,
      });
      setShowForm(true);
    }
  };

  const deleteTemplate = async (t) => {
    if (t.is_system) {
      toast.error('System templates cannot be deleted.');
      return;
    }
    if (!window.confirm('Delete this template?')) return;
    const { error } = await marketingService.deleteTemplate(t.id, { role: 'admin', userId: profile?.id });
    if (error) {
      toast.error(error.message ?? 'Failed to delete');
      return;
    }
    toast.success('Template deleted');
    loadTemplates();
    if (editingId === t.id) {
      setShowForm(false);
      navigate('/marketing/email/templates');
    }
  };

  const handleJsonUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !file.name.toLowerCase().endsWith('.json')) {
      toast.error('Please select a .json file');
      e.target.value = '';
      return;
    }
    file.text().then((text) => {
      try {
        const json = JSON.parse(text);
        setForm((f) => ({
          ...f,
          json_definition: json,
          html_body: typeof json.html_body === 'string' ? json.html_body : (f.html_body || ''),
          subject: typeof json.subject === 'string' ? json.subject : (f.subject || ''),
        }));
        toast.success('JSON imported');
      } catch {
        toast.error('Invalid JSON');
      }
      e.target.value = '';
    });
  };

  const handleJsonExport = () => {
    const obj = {
      name: form.name,
      description: form.description,
      category: form.category,
      subject: form.subject,
      preview_text: form.preview_text,
      html_body: form.html_body,
      variables_schema: form.variables_schema,
      json_definition: form.json_definition,
    };
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `template-${(form.name || 'export').replace(/\s+/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template exported');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex flex-wrap justify-between items-center gap-4">
        <h3 className="text-lg font-semibold text-slate-800">Email Templates</h3>
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-600">Language</label>
          <select
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 text-sm"
          >
            <option value="">All</option>
            {LANGUAGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            New template
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-gray-100">
              <tr className="text-left text-slate-600">
                <th className="p-4 font-medium">Name</th>
                <th className="p-4 font-medium">Category</th>
                <th className="p-4 font-medium">Language</th>
                <th className="p-4 font-medium">Trigger</th>
                <th className="p-4 font-medium">System</th>
                <th className="p-4 font-medium">Active</th>
                <th className="p-4 font-medium">Created</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-500">
                    No templates yet.
                  </td>
                </tr>
              ) : (
                templates.map((t) => (
                  <tr key={t.id} className="border-b border-gray-50 hover:bg-slate-50/50">
                    <td className="p-4 font-medium text-slate-800">{t.name}</td>
                    <td className="p-4 text-slate-600">{t.category || '—'}</td>
                    <td className="p-4 text-slate-600">{LANGUAGE_OPTIONS.find((o) => o.value === t.language)?.label ?? t.language ?? '—'}</td>
                    <td className="p-4 text-slate-600">{t.trigger_event || '—'}</td>
                    <td className="p-4">{t.is_system ? 'Yes' : '—'}</td>
                    <td className="p-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${t.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                        {t.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4 text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</td>
                    <td className="p-4 flex gap-2">
                      <button onClick={() => openEdit(t)} className="p-2 text-slate-400 hover:text-emerald-600 rounded-lg" title="Edit"><SafeIcon icon={FiEdit} className="w-4 h-4" /></button>
                      <button onClick={() => duplicateTemplateRow(t)} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg" title="Duplicate"><SafeIcon icon={FiCopy} className="w-4 h-4" /></button>
                      {!t.is_system && (
                        <button onClick={() => deleteTemplate(t)} className="p-2 text-slate-400 hover:text-red-600 rounded-lg" title="Delete"><SafeIcon icon={FiTrash2} className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto" onClick={() => { setShowForm(false); navigate('/marketing/email/templates'); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">{editingId ? 'Edit template' : 'New template'}</h3>
              <button onClick={() => { setShowForm(false); navigate('/marketing/email/templates'); }} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg"><SafeIcon icon={FiX} className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                    placeholder="Template name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value || 'blank'} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Language</label>
                  <select
                    value={form.language || 'es'}
                    onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                  >
                    {LANGUAGE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                  rows={2}
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                  placeholder="Email subject (use {{variable}})"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Preview text (preheader)</label>
                <input
                  type="text"
                  value={form.preview_text}
                  onChange={(e) => setForm((f) => ({ ...f, preview_text: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">HTML body</label>
                <textarea
                  value={form.html_body}
                  onChange={(e) => setForm((f) => ({ ...f, html_body: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 font-mono text-sm"
                  rows={14}
                  placeholder="<html>... Use {{student_name}}, {{event_title}}, etc.</html>"
                />
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Variables schema</label>
                  <p className="text-xs text-slate-500 mb-2">
                    Use variables in your template as <code className="bg-slate-100 px-1 rounded">&#123;&#123;variable_key&#125;&#125;</code> (e.g. &#123;&#123;student_name&#125;&#125;). Keys use snake_case (e.g. student_name).
                  </p>

                  {/* Predefined variables dropdown */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-slate-500 mb-1">Add from predefined</label>
                    <select
                      value={predefinedSelect}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val) addPredefinedVariable(val);
                      }}
                      className="w-full max-w-md px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 text-sm"
                    >
                      <option value="">Choose a variable…</option>
                      {PREDEFINED_VARIABLE_GROUPS.map((g) => (
                        <optgroup key={g.group} label={g.group}>
                          {g.variables.map((v) => (
                            <option key={v.key} value={v.key}>
                              {v.key} — {v.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  {/* Added variables as removable chips with usage hint */}
                  {(form.variables_schema || []).length > 0 && (
                    <div className="mb-3">
                      <span className="block text-xs font-medium text-slate-500 mb-2">Added variables</span>
                      <div className="flex flex-wrap gap-2">
                        {(form.variables_schema || []).map((v, i) => (
                          <span
                            key={v.key}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-800 rounded-full text-sm border border-emerald-200"
                          >
                            <span className="font-mono text-xs" title={`Use in template: {{${v.key}}}`}>
                              &#123;&#123;{v.key}&#125;&#125;
                            </span>
                            <span className="text-slate-600">{v.label}</span>
                            {v.required && <span className="text-amber-600 text-xs">required</span>}
                            <button
                              type="button"
                              onClick={() => removeVariable(i)}
                              className="ml-0.5 p-0.5 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                              title="Remove variable"
                            >
                              <SafeIcon icon={FiX} className="w-3.5 h-3.5" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add custom variable */}
                  <div className="pt-2 border-t border-gray-100">
                    <span className="block text-xs font-medium text-slate-500 mb-2">Add custom variable</span>
                    <div className="flex flex-wrap gap-2 items-end">
                      <input
                        type="text"
                        value={varKey}
                        onChange={(e) => setVarKey(e.target.value)}
                        placeholder="e.g. student_name"
                        className="flex-1 min-w-[140px] px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm font-mono"
                      />
                      <input
                        type="text"
                        value={varLabel}
                        onChange={(e) => setVarLabel(e.target.value)}
                        placeholder="e.g. Student Name"
                        className="flex-1 min-w-[140px] px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                      />
                      <label className="flex items-center gap-1.5 whitespace-nowrap text-sm text-slate-600">
                        <input
                          type="checkbox"
                          checked={varRequired}
                          onChange={(e) => setVarRequired(e.target.checked)}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        Required
                      </label>
                      <button
                        type="button"
                        onClick={addVariable}
                        className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  className="hidden"
                  onChange={handleJsonUpload}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50"
                >
                  <SafeIcon icon={FiUpload} className="w-4 h-4" />
                  Upload JSON
                </button>
                <button
                  type="button"
                  onClick={handleJsonExport}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50"
                >
                  <SafeIcon icon={FiDownload} className="w-4 h-4" />
                  Export JSON
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50"
                >
                  <SafeIcon icon={FiEye} className="w-4 h-4" />
                  Preview
                </button>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm font-medium text-slate-700">Active</span>
              </label>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => { setShowForm(false); navigate('/marketing/email/templates'); }} className="px-4 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50">Cancel</button>
              <button onClick={saveTemplate} disabled={saving} className="px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50">
                <SafeIcon icon={FiSave} className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {previewOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" onClick={() => setPreviewOpen(false)}>
          <div
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <h3 className="font-semibold">Preview</h3>
              <button onClick={() => setPreviewOpen(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg">×</button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 bg-slate-50">
              <div className="bg-white rounded-lg shadow-sm p-4 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: renderSampleBody(form.html_body, form.variables_schema) }} />
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default MarketingEmailTemplates;
